import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { onAutocompleteChangeInForm } from 'app/shared/componentsRestyled/coreDesignSystem/form';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import useForm from 'hooks/useForm';
import { getConditionalObject } from 'utils/data';

import { openConfirmationDialogAction } from 'store/global/actions';
import { selectLoanDenialReasons } from 'store/loanApplications/selectors';
import { selectDenialReasonsData, selectDenialReasonsOptions } from 'store/system/selectors';

import { getBorrowerFieldName, loanDenialFormSchema } from './constants';
import { getInitialFormValues } from './utils';

const useLoanDenialForm = ({ editData, borrowerOptions, saveForm, cancelForm }) => {
  const dispatch = useDispatch();

  const loanDenialReasons = useSelector(selectLoanDenialReasons);
  const denialReasonOptions = useSelector(selectDenialReasonsOptions);
  const allDenialReasonsData = useSelector(selectDenialReasonsData);

  const filteredDenialReasonOptions = useMemo(
    () =>
      denialReasonOptions.filter(
        ({ value }) => !loanDenialReasons.some(({ denialReason }) => denialReason.id === value),
      ),
    [loanDenialReasons, denialReasonOptions],
  );

  const initialFormValues = useMemo(
    () =>
      editData ? getInitialFormValues({ editData, denialReasonOptions, borrowerOptions }) : {},
    [editData, denialReasonOptions, borrowerOptions],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setInForm,
    setForm,
    setInitialFormValues,
    isFormChanged,
    validateField,
    validateForm,
    setSchema,
    setValidationErrors,
    setIsTouched,
  } = useForm(initialFormValues);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onLoanDenialReasonChange = useCallback(
    (e, value) => {
      onAutocompleteChange(e, value);

      if (!value) {
        return;
      }

      const denialReason = allDenialReasonsData[value?.value];

      setForm({
        ...denialReason,
        name: denialReasonOptions.find(({ value }) => denialReason.id === value),
        ...getConditionalObject(denialReason.isDenialDueToCredit, () =>
          borrowerOptions.reduce((acc, __, i) => {
            acc[getBorrowerFieldName(i)] = borrowerOptions.length === 1;

            return acc;
          }, {}),
        ),
      });
    },
    [allDenialReasonsData, denialReasonOptions, borrowerOptions, setForm, onAutocompleteChange],
  );

  const isDenialReasonSelected = !!form.name;

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      saveForm({ initialId: editData?.id, updatedData: form, isAdded: editData?.isAdded });
    },
    [form, editData, validateForm, saveForm],
  );

  const onCancel = useCallback(() => {
    if (!isFormChanged) {
      cancelForm(editData);

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Cancel Changes',
        description: 'Are you sure you want to cancel changes?',
        onAccept: () => {
          cancelForm(editData);
        },
      }),
    );
  }, [cancelForm, editData, dispatch, isFormChanged]);

  useEffect(() => {
    setForm(initialFormValues);
    setInitialFormValues(initialFormValues);
    setSchema(loanDenialFormSchema);
    setValidationErrors({});
    setIsTouched(false);
  }, [
    initialFormValues,
    setForm,
    setInitialFormValues,
    setSchema,
    setValidationErrors,
    setIsTouched,
  ]);

  return {
    form,
    handleChange,
    validationErrors,
    isFormChanged,
    onSubmit,
    onCancel,
    onLoanDenialReasonChange,
    filteredDenialReasonOptions,
    isDenialReasonSelected,
  };
};

export default useLoanDenialForm;
export { getBorrowerFieldName } from './constants';
